<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">文章创作</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.task_name" placeholder="请输入任务名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增任务
        </el-button>
      </div>

      <!-- 任务列表 -->
      <el-table :data="taskList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="task_name" label="任务名称" width="200" />

        <el-table-column label="已创作" width="120">
          <template #default="{ row }">
            <el-tag type="warning">{{ row.total_num }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="下拉词库" width="150">
          <template #default="{ row }">
            {{ getTitleName(row.title_id) }}
          </template>
        </el-table-column>


        <el-table-column label="错误提示" width="180">
          <template #default="{ row }">
            <el-popover v-if="row.error_message" placement="top-start" width="300" trigger="hover">
              <template #reference>
                <el-tag type="danger" size="small" style="cursor: pointer">
                  <el-icon>
                    <Warning />
                  </el-icon>
                  查看错误
                </el-tag>
              </template>
              <div class="error-message-content">
                {{ row.error_message }}
              </div>
            </el-popover>
            <span v-else class="empty-value">无错误</span>
          </template>
        </el-table-column>
        <el-table-column label="分类" width="150">
          <template #default="{ row }">
            {{ getCategoryName(row.article_category_id) }}
          </template>
        </el-table-column>
        <el-table-column label="图库" width="150">
          <template #default="{ row }">
            {{ getGalleryName(row.tuku_id) }}
          </template>
        </el-table-column>
        <el-table-column label="AI接口" width="150">
          <template #default="{ row }">
            {{ getAiApiName(row.ai_map_id || -1) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.article_status)">
              {{ getStatusText(row.article_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="article_limit" label="生成上限" width="100" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <!-- 新增任务状态列 -->
        <el-table-column label="任务状态" width="120" fixed="right">
          <template #default="{ row }">
            <el-tag :type="getTaskStatusTagType(row.task_status)" effect="dark">
              {{ getTaskStatusText(row.task_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 在任务状态列后面新增重试列 -->
        <el-table-column label="重试" width="120" fixed="right">
          <template #default="{ row }">
            <el-button type="warning" size="small" :disabled="row.task_status === 1 && row.transfer_status === 1"
              @click="handleRetry(row.id)">
              <el-icon>
                <Refresh />
              </el-icon>
              重试
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchTaskList" @current-change="fetchTaskList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px" :close-on-click-modal="false">
      <el-form ref="taskForm" :model="taskForm" label-width="140px" :rules="formRules">
        <el-form-item label="任务名称" prop="task_name">
          <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" size="large" />
        </el-form-item>

        <!-- 修改后的文章标题下拉框 -->
        <el-form-item label="下拉词库" prop="title_id">
          <el-select v-model="taskForm.title_id" placeholder="请选择文章标题" style="width: 100%" size="large" filterable>
            <el-option v-for="title in titleOptions" :key="title.id" :label="title.title_name" :value="title.id" />
          </el-select>
        </el-form-item>

        <!-- 新增：标题算法选择器 -->
        <el-form-item label="标题算法" prop="title_algorithm">
          <el-radio-group v-model="taskForm.title_algorithm">
            <el-radio :label="1">未使用标题</el-radio>
            <el-radio :label="2">重复使用标题</el-radio>
          </el-radio-group>
        </el-form-item>


        <el-form-item label="AI作图" prop="ai_map_id">
          <el-select v-model="taskForm.ai_map_id" placeholder="请选择AI接口" style="width: 100%" filterable>
            <!-- 添加"不选择模板"选项 -->
            <el-option label="不选择AI作图" :value="-1" />
            <el-option v-for="medium in mediums" :key="medium.id" :label="medium.medium_name" :value="medium.id" />
          </el-select>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="文章分类" prop="article_category_id">
              <el-select v-model="taskForm.article_category_id" placeholder="请选择分类" style="width: 100%"
                @change="handleCategoryChange" filterable>
                <el-option v-for="category in categories" :key="category.id" :label="category.article_category"
                  :value="category.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="图库" prop="tuku_id">
              <el-select v-model="taskForm.tuku_id" placeholder="请选择图库" style="width: 100%" filterable>
                <el-option v-for="gallery in galleries" :key="gallery.id" :label="gallery.tuku_name"
                  :value="gallery.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20" v-if="hasKnowledgePlugin">
          <el-col :span="12">
            <el-form-item label="知识库素材">
              <el-select v-model="taskForm.knowledge_category_id" placeholder="可选，关联知识库" style="width: 100%" filterable clearable>
                <el-option label="不关联知识库" :value="0" />
                <el-option v-for="kc in knowledgeCategories" :key="kc.id" :label="kc.category_name" :value="kc.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <!-- 占位，保持布局对齐 -->
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="配图数量" prop="article_tp_num">
              <el-input-number v-model="taskForm.article_tp_num" :min="0" :max="10" controls-position="right"
                style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="模板样式" prop="moban_style">
              <el-select v-model="taskForm.moban_style" placeholder="请选择模板" style="width: 100%" filterable>
                <!-- 添加"不选择模板"选项 -->
                <el-option label="不选择模板" :value="0" />
                <el-option v-for="moban in mobanStyles" :key="moban.id" :label="moban.moban_name" :value="moban.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="写作模型" prop="article_write_model_id">
              <el-select v-model="taskForm.article_write_model_id" placeholder="请选择写作模型" style="width: 100%" filterable>
                <el-option label="不选择模型" :value="-1" />
                <el-option v-for="model in writingModels.filter(d => d.writing_type === '1')" :key="model.id"
                  :label="model.writing_name" :value="model.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="写作指令" prop="article_instruct_model_id">
              <el-select v-model="taskForm.article_instruct_model_id" placeholder="请选择写作指令" style="width: 100%"
                filterable>
                <el-option v-for="instruct in titleInstructs.filter(d => d.instruct_type === 1)" :key="instruct.id"
                  :label="instruct.instruct_name" :value="instruct.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="标题模型" prop="title_write_model_id">
              <el-select v-model="taskForm.title_write_model_id" placeholder="请选择标题模型" style="width: 100%" filterable>
                <el-option label="不选择模型" :value="-1" />
                <el-option v-for="model in writingModels.filter(d => d.writing_type === '2')" :key="model.id"
                  :label="model.writing_name" :value="model.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="标题指令" prop="title_instruct_model_id">
              <el-select v-model="taskForm.title_instruct_model_id" placeholder="请选择标题指令" style="width: 100%"
                filterable>
                <el-option v-for="instruct in titleInstructs.filter(d => d.instruct_type === 2)" :key="instruct.id"
                  :label="instruct.instruct_name" :value="instruct.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="文章状态" prop="article_status">
              <el-radio-group v-model="taskForm.article_status">
                <el-radio :label="1">待审核</el-radio>
                <el-radio :label="2">正常</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="文章上限" prop="article_limit">
              <el-input-number v-model="taskForm.article_limit" :min="1" :max="200" controls-position="right"
                style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="备注信息">
          <el-input v-model="taskForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTaskForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Refresh, Warning
} from '@element-plus/icons-vue'

export default {
  name: 'ArticleCreate',
  components: { Search, Plus, Edit, Delete, Refresh, Warning },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增任务',
      isEditMode: false,
      searchForm: {
        task_name: ''
      },
      taskForm: {
        id: null,
        task_name: '',
        article_category_id: null,
        title_type: 1,
        tuku_id: null,
        ai_map_id: null,
        article_tp_num: 1,
        moban_style: 1,
        article_write_model_id: null,
        article_instruct_model_id: null,
        title_write_model_id: null,
        title_instruct_model_id: null,
        article_status: 1,
        article_limit: 10,
        title_id: null, // 用于存储选中的标题ID
        title_algorithm: 1,// 默认选择"未使用标题"
        knowledge_category_id: 0,
        notes: ''
      },
      formRules: {
        task_name: [
          { required: true, message: '请输入任务名称', trigger: 'blur' }
        ],
        title_id: [
          { required: true, message: '请选择文章标题', trigger: 'change' }
        ],
        article_category_id: [
          { required: true, message: '请选择文章分类', trigger: 'change' }
        ],
        tuku_id: [
          { required: true, message: '请选择图库', trigger: 'change' }
        ],
        article_write_model_id: [
          // 非必选，不选时使用 la_config 默认配置
        ],
        article_instruct_model_id: [
          { required: true, message: '请选择写作指令', trigger: 'change' }
        ],
        title_write_model_id: [
          // 非必选，不选时使用 la_config 默认配置
        ],
        title_instruct_model_id: [
          { required: true, message: '请选择标题指令', trigger: 'change' }
        ],
        article_limit: [
          { required: true, message: '请输入文章上限', trigger: 'blur' }
        ],
        title_algorithm: [
          { required: true, message: '请选择标题算法', trigger: 'change' }
        ]
      },
      taskList: [],
      categories: [],
      galleries: [],
      writingModels: [],
      mobanStyles: [], // 存储从接口获取的模板数据
      titleInstructs: [],
      titleOptions: [], // 添加标题选项列表
      knowledgeCategories: [], // 知识库分类列表
      hasKnowledgePlugin: false, // 是否开通知识云库插件
      mediums: [], // AI接口列表

      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    titleNameMap() {
      const map = {};
      this.titleOptions.forEach(title => {
        map[title.id] = title.title_name;
      });
      return map;
    },
    // 分类映射
    categoryMap() {
      const map = {};
      this.categories.forEach(category => {
        map[category.id] = category.article_category;
      });
      return map;
    },
    // 添加模板映射
    mobanMap() {
      const map = {};
      this.mobanStyles.forEach(moban => {
        map[moban.id] = moban;
      });
      return map;
    },
    // 图库映射
    galleryMap() {
      const map = {};
      this.galleries.forEach(gallery => {
        map[gallery.id] = gallery.tuku_name;
      });
      return map;
    },


    AiApiMap() {
      const map = {};
      (this.mediums || []).forEach(medium => {
        map[medium.id] = medium.medium_name;
      });
      return map;
    },
    // 知识库分类映射
    knowledgeCategoryMap() {
      const map = {};
      this.knowledgeCategories.forEach(kc => {
        map[kc.id] = kc.category_name;
      });
      return map;
    },
  },
  mounted() {
    // 先加载必要的数据
    this.fetchAiApiList().then(() => {
      // 数据加载完成后再获取任务列表
      this.fetchTaskList();
    });
    this.fetchCategories();
    this.fetchGalleries();
    this.fetchWritingModels();
    this.fetchTitleInstructs();
    this.fetchMobanStyles(); // 新增：获取模板样式
    this.fetchTitleList(); // 新增：获取标题列表
    this.fetchAiApiList();
    this.checkKnowledgePlugin(); // 检查知识云库插件权限（内部会拉取分类）



  },
  methods: {
    // 获取标题名称
    getTitleName(id) {
      return this.titleNameMap[id] || '未知标题';
    },

    // 处理重试任务
    async handleRetry(id) {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_ai_create_management.php`,
          {
            action: 'retry',
            id: id,
            store_id: this.userInfo.id
          }
        );

        if (data.success) {
          this.$message.success('任务已重置为待启动状态');
          this.fetchTaskList(); // 刷新列表
        } else {
          this.$message.error(data.error || '重试任务失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '重试任务失败';
        this.$message.error(`重试失败: ${errorMsg}`);
        console.error('重试错误:', error);
      }
    },
    // 获取任务状态文本
    getTaskStatusText(status) {
      switch (status) {
        case 1: return '待启动';
        case 2: return '运行中';
        case 3: return '已完成';
        case 4: return '失败';
        default: return '未知';
      }
    },

    // 获取任务状态标签颜色
    getTaskStatusTagType(status) {
      switch (status) {
        case 1: return 'info';       // 待启动 - 蓝色
        case 2: return 'warning';    // 运行中 - 橙色
        case 3: return 'success';    // 已完成 - 绿色
        case 4: return 'danger';     // 失败 - 红色
        default: return '';
      }
    },

    // 新增：获取标题列表
    async fetchTitleList() {
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 1000 // 获取足够多的标题
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/title_management.php`,
          params
        );

        if (data.data) {
          this.titleOptions = data.data;
        }
      } catch (error) {
        console.error('获取标题列表错误:', error);
      }
    },

    async fetchAiApiList() {
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 1000
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/medium_model_key.php`,
          params
        );

        if (data.data) {
          // 确保赋值给 mediums
          this.mediums = data.data;
          console.log('AI接口列表加载完成:', this.mediums); // 调试用
        }
      } catch (error) {
        console.error('获取AI接口列表错误:', error);
        this.$message.error('获取AI接口列表失败');
      }
    },
    // 新增：获取模板样式
    async fetchMobanStyles() {
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 100 // 获取所有模板
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/moban_manager.php`,
          params
        );

        if (data.data) {
          this.mobanStyles = data.data;

        }
      } catch (error) {
        console.error('获取模板样式错误:', error);
      }
    },
    // 新增方法：处理API错误
    handleApiError(error) {
      console.error('API请求错误:', error);

      // 检查是否为响应错误
      if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 401:
            this.$message.error('身份验证失败，请重新登录');
            break;
          case 403:
            this.$message.error('操作权限不足');
            break;
          case 500:
            this.$message.error(`服务器内部错误: ${data.message || '请联系管理员'}`);
            break;
          default:
            this.$message.error(data.error || data.message || '未知错误');
        }
      } else if (error.request) {
        // 请求已发送但无响应
        this.$message.error('服务器响应超时，请稍后再试');
      } else {
        // 其他错误
        this.$message.error(`请求失败: ${error.message}`);
      }
    },
    // 获取状态标签
    getStatusTag(status) {
      switch (status) {
        case 1: return 'info';
        case 2: return 'success';
        default: return '';
      }
    },

    // 获取状态文本
    getStatusText(status) {
      switch (status) {
        case 1: return '待审核';
        case 2: return '正常';
        default: return '未知';
      }
    },

    // 获取分类名称
    getCategoryName(id) {
      return this.categoryMap[id] || '未知分类';
    },

    // 获取图库名称
    getGalleryName(id) {
      return this.galleryMap[id] || '未知图库';
    },
    getAiApiName(id) {
      // 处理空值情况
      if (id === -1 || id === null || id === undefined) {
        return '不选择AI';
      }

      // 确保 mediums 已加载
      if (!this.mediums || this.mediums.length === 0) {
        return '加载中...';
      }

      // 统一转换为字符串比较（解决类型不一致问题）
      const found = this.mediums.find(item => String(item.id) === String(id));

      return found ? found.medium_name : '未知AI接口';
    },

    // 获取任务列表
    async fetchTaskList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.task_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        // 如果有搜索条件，加入搜索参数
        if (this.searchForm.task_name) {
          params.task_name = this.searchForm.task_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_ai_create_management.php`,
          params
        );

        if (data.data) {
          this.taskList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取任务列表失败');
        console.error('获取任务列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取分类列表
    async fetchCategories() {
      try {
        const params = {
          action: 'list_all',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 100
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_category_management.php`,
          params
        );

        if (data.data) {
          this.categories = data.data;
        }
      } catch (error) {
        console.error('获取分类列表错误:', error);
      }
    },

    // 获取图库列表
    async fetchGalleries() {
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 100
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/library_management.php`,
          params
        );

        if (data.data) {
          this.galleries = data.data;
        }
      } catch (error) {
        console.error('获取图库列表错误:', error);
      }
    },

    // 获取写作模型列表
    async fetchWritingModels() {
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 100
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/WritingModelKeyController.php`,
          params
        );

        if (data.data) {
          this.writingModels = data.data;
        }
      } catch (error) {
        console.error('获取写作模型列表错误:', error);
      }
    },

    // 获取标题指令列表
    async fetchTitleInstructs() {
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 100
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/CreationInstructionsController.php`,
          params
        );

        if (data.data) {
          this.titleInstructs = data.data;
        }
      } catch (error) {
        console.error('获取标题指令列表错误:', error);
      }
    },

    // 检查知识云库插件权限（完成后自动拉取分类）
    async checkKnowledgePlugin() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/check_plugin.php`,
          { store_id: this.userInfo.id, plugin: 'rwjh_zsk' }
        );
        if (data.data) {
          this.hasKnowledgePlugin = data.data.rwjh_zsk === true;
        }
        // 权限结果返回后再拉取分类
        if (this.hasKnowledgePlugin) {
          await this.fetchKnowledgeCategories();
        }
      } catch (error) {
        console.error('检查知识云库插件权限错误:', error);
      }
    },

    // 获取知识库分类列表
    async fetchKnowledgeCategories() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/KnowledgeBaseController.php`,
          {
            action: 'list_categories',
            store_id: this.userInfo.id
          }
        );
        if (data.data) {
          this.knowledgeCategories = data.data;
        }
      } catch (error) {
        console.error('获取知识库分类列表错误:', error);
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchTaskList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增任务';
      this.isEditMode = false;
      this.taskForm = {
        id: null,
        task_name: '',
        title_id: null, // 清空标题选择
        title_algorithm: 1, // 默认"未使用标题"
        article_category_id: null,
        title_type: 1,
        tuku_id: null,
        ai_map_id: -1,
        article_tp_num: 1,
        moban_style: 0,
        article_write_model_id: null,
        article_instruct_model_id: null,
        title_write_model_id: null,
        title_instruct_model_id: null,
        article_status: 2,
        article_limit: 10,
        knowledge_category_id: 0,
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑任务';
      this.isEditMode = true;
      this.taskForm = {
        id: row.id,
        task_name: row.task_name,
        title_id: row.title_id, // 使用标题ID
        title_algorithm: row.title_algorithm || 1, // 新增字段
        article_category_id: row.article_category_id,
        title_type: row.title_type,
        tuku_id: row.tuku_id,
        ai_map_id: row.ai_map_id === null ? -1 : row.ai_map_id, // 如果原值为null，则设为-1
        article_tp_num: row.article_tp_num,
        moban_style: row.moban_style,
        article_write_model_id: row.article_write_model_id,
        article_instruct_model_id: row.article_instruct_model_id,
        title_write_model_id: row.title_write_model_id,
        title_instruct_model_id: row.title_instruct_model_id,
        article_status: row.article_status,
        article_limit: row.article_limit,
        knowledge_category_id: row.knowledge_category_id || 0,
        notes: row.notes
      };
      this.dialogVisible = true;
    },

    // 提交表单（新增/编辑）
    submitTaskForm() {
      this.$refs.taskForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.taskForm,
              store_id: this.userInfo.id
            };

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/article_ai_create_management.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchTaskList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },



    // 删除任务
    async handleDelete(id) {
      this.$confirm('确定要删除该任务吗？该任务创建的文章都将会被删除！', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/article_ai_create_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchTaskList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/page-common.scss' as *;
</style>